import {
  Modal,
  Box,
  Typography,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import moment from 'moment';
import { PaymentStatusResponse } from '../../../../packages/crypto-payments/src/models/types';

interface PaymentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  payment: PaymentStatusResponse | null;
}

const PaymentDetailsModal = ({
  open,
  onClose,
  payment,
}: PaymentDetailsModalProps) => {
  if (!payment) return null;

  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="payment-details-modal"
      aria-describedby="modal-showing-payment-transaction-details"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '60%', lg: '50%' },
          maxWidth: '600px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <MDTypography variant="h5" fontWeight="medium">
            Payment Details
          </MDTypography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </MDBox>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Payment ID
            </MDTypography>
            <MDTypography variant="body2">{payment.paymentId}</MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Status
            </MDTypography>
            <MDTypography
              variant="body2"
              color={getStatusColor(payment.status)}
            >
              {payment.status.toUpperCase()}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Currency
            </MDTypography>
            <MDTypography variant="body2">{payment.currency}</MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Amount
            </MDTypography>
            <MDTypography variant="body2">{payment.amount}</MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Created At
            </MDTypography>
            <MDTypography variant="body2">
              {moment(payment.createdAt).format('DD/MM/YYYY HH:mm:ss')}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Updated At
            </MDTypography>
            <MDTypography variant="body2">
              {moment(payment.updatedAt).format('DD/MM/YYYY HH:mm:ss')}
            </MDTypography>
          </Grid>
          {payment.confirmedAt && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Confirmed At
              </MDTypography>
              <MDTypography variant="body2">
                {moment(payment.confirmedAt).format('DD/MM/YYYY HH:mm:ss')}
              </MDTypography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Payment Identifier
            </MDTypography>
            <MDTypography variant="body2">
              {payment.paymentIdentifier || 'N/A'}
            </MDTypography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              Address
            </MDTypography>
            <MDTypography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {payment.address}
            </MDTypography>
          </Grid>
          {payment.transactionHash && (
            <Grid item xs={12}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Transaction Hash
              </MDTypography>
              <MDTypography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {payment.transactionHash}
              </MDTypography>
            </Grid>
          )}
          {payment.confirmations !== undefined && (
            <Grid item xs={12} sm={6}>
              <MDTypography variant="caption" color="text" fontWeight="bold">
                Confirmations
              </MDTypography>
              <MDTypography variant="body2">
                {payment.confirmations}
              </MDTypography>
            </Grid>
          )}
        </Grid>

        <MDBox mt={4} display="flex" justifyContent="flex-end">
          <MDButton variant="gradient" color="info" onClick={onClose}>
            Close
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );
};

export default PaymentDetailsModal;
